import {
  readAgentConversation,
  sendAgentConversationMessage,
  stopAgentConversation,
} from '@commenda/api/commenda-ai/agentConversationsApi';
import {
  commendaAiPendingKey,
  deleteCommendaAiConversation,
  getBoundProfileKey,
  getBrowserTimeZone,
  profileTakesAttachments,
  renameCommendaAiConversation,
  updateCommendaAiConversationVisibility,
  type CommendaAiConversation as CommendaAiConversationRecord,
} from '@commenda/api/commenda-ai/api';
import {
  COMMENDA_CHAT_PROFILE_KEY,
  WORKFLOW_BUILDER_PROFILE_KEY,
} from '@commenda/api/commenda-ai/constants';
import type { IDocumentReaderUploadReference } from '@commenda/api/commenda-ai/document-reader/types';
import type {
  AgentConversationAnswerTo,
  CommendaAiConversationAccessView,
  CommendaAiConversationVisibility,
  CommendaAiPageContext,
  CommendaAiUploadFile,
  SendAgentConversationMessageBody,
} from '@commenda/api/commenda-ai/types';
import { useCommendaAiChat } from '@commenda/api/commenda-ai/useCommendaAiChat';
import { useCommendaAiConversationFeedback } from '@commenda/api/commenda-ai/useCommendaAiConversationFeedback';
import { useCommendaAiConversations } from '@commenda/api/commenda-ai/useCommendaAiConversations';
import { signOut } from '@commenda/auth';
import { Button, toast } from '@commenda/kakashi';
import type { AgentAttachment } from '@commenda/types';
import { cn } from '@commenda/utils';
import type { UIMessage } from 'ai';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStickToBottomContext } from 'use-stick-to-bottom';
import { GripResizeHandle, useResizablePane } from '../resizable-pane';
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from './ai-elements/conversation';
import { Message, MessageContent } from './ai-elements/message';
import { Shimmer } from './ai-elements/shimmer';
import {
  AttachmentPreviewPanel,
  type PreviewFile,
} from './AttachmentPreviewPanel';
import { ChatComposer } from './ChatComposer';
import { ChatDropZone } from './ChatDropZone';
import {
  CommendaAiActionsContext,
  type CommendaAiActions,
  type CommendaAiLiveCard,
  type CommendaAiQuestionAnswer,
} from './CommendaAiActionsContext';
import { useCommendaAiHost } from './CommendaAiHostProvider';
import { CommendaAiMessageItem } from './CommendaAiMessageItem';
import {
  ComposerClientStrip,
  ComposerControlsHelper,
  ComposerFooterControls,
  type IComposerControlsConfig,
} from './composer-controls';
import { ConversationAccessHelper } from './conversation-access-helper';
import { ConversationHeader } from './ConversationHeader';
import { ChatDocumentMentionsHelper } from './markdown/chat-document-mentions';
import type { IChatDocumentMention } from './markdown/chat-document-mentions.types';
import { QuestionBlockHelper } from './question-block';
import { QuestionDockedCard } from './QuestionDockedCard';
import { ThinkingOrb } from './ThinkingOrb';
import { useCommendaAiWorkspace } from './useCommendaAiWorkspace';
import { useDocumentProcessingNotice } from './useDocumentProcessingNotice';

const TITLE_REVALIDATE_DELAYS_MS = [0, 1500, 4000];

const draftKey = (conversationId: string) =>
  `commenda-ai:draft:${conversationId}`;

/** The key a draft was saved under before the rename, read once so a draft saved across the deploy survives. */
const legacyDraftKey = (conversationId: string) =>
  `mcp-chat:draft:${conversationId}`;

// iOS-drawer ease-out (Ionic's curve) for the preview panel: fast start, gentle settle.
const PANEL_EASE: [number, number, number, number] = [0.32, 0.72, 0, 1];

const PREVIEW_PANEL_MIN_WIDTH = 400;
const PREVIEW_PANEL_MAX_WIDTH = 1100;
const PREVIEW_PANEL_DEFAULT_WIDTH = 640;

function messageText(message: UIMessage): string {
  return message.parts
    .filter(
      (part): part is Extract<UIMessage['parts'][number], { type: 'text' }> =>
        part.type === 'text'
    )
    .map((part) => part.text)
    .join('');
}

/** For each message, the text of the first user message after it (null when none). One backward
 * pass; feeds the per-message position context question cards use for live vs answered state. */
function followingUserTextByIndex(messages: UIMessage[]): (string | null)[] {
  const result: (string | null)[] = new Array(messages.length).fill(null);
  let nextUserText: string | null = null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    result[index] = nextUserText;
    if (messages[index].role === 'user')
      nextUserText = messageText(messages[index]);
  }
  return result;
}

function lastUserText(messages: UIMessage[]): string {
  const lastUser = [...messages]
    .reverse()
    .find((message) => message.role === 'user');
  return lastUser ? messageText(lastUser) : '';
}

/** AgentAttachment[] → AI SDK file parts for useChat's sendMessage({ files }). The stored (private
 * S3) URL rides through; the backend signs it just-in-time before the model reads it. */
function toFileParts(attachments: AgentAttachment[]) {
  return attachments.map((attachment) => ({
    type: 'file' as const,
    url: attachment.url,
    mediaType: attachment.mimeType,
    filename: attachment.fileName,
  }));
}

/** Rendered inside <Conversation> to lift its stick-to-bottom `scrollToBottom` up to the parent, so
 * posting a message can force a scroll to the newest content even when the user has scrolled up. */
function ScrollToBottomBridge({
  register,
}: {
  register: (scrollToBottom: () => void) => void;
}) {
  const { scrollToBottom } = useStickToBottomContext();
  useEffect(() => {
    register(() => {
      scrollToBottom();
    });
  }, [scrollToBottom, register]);
  return null;
}

function ConversationTopFadeTracker({
  onChange,
}: {
  onChange: (scrolledFromTop: boolean) => void;
}) {
  const { scrollRef } = useStickToBottomContext();
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const update = () => onChange(el.scrollTop > 8);
    update();
    el.addEventListener('scroll', update, { passive: true });
    return () => el.removeEventListener('scroll', update);
  }, [scrollRef, onChange]);
  return null;
}

/** Commenda Agent chat surface (AI Elements). Text parts render as markdown, tool parts as Tool
 * cards. Handles: the "create on send" first-message flush, stop (via the composer), a FAILED turn
 * with retry, and a mid-conversation 401 that hands off to the app's sign-out flow while stashing
 * the in-flight prompt so it survives the redirect. */
export function CommendaAiConversation({
  conversationId,
  initialMessages,
  loadedConversation = null,
  headerTrailing,
  composerControls,
  layout = 'page',
  renderHeader,
  contextSlot,
  getPageContext,
  afterDeleteHref = '/agent',
  buildSendBody,
  onGeneratingChange,
  onMessagesChange,
  beforeSend,
}: {
  conversationId: string;
  initialMessages: UIMessage[];
  /** The record fetched with the history; stands in until the shared list has it, and is all
   * there is for a run session, which the list leaves out. */
  loadedConversation?: CommendaAiConversationRecord | null;
  headerTrailing?: React.ReactNode;
  /** The client, agent, mode and model pickers this surface offers. The agent shows as a fixed
   * chip here: it was chosen when the conversation was created. */
  composerControls?: IComposerControlsConfig;
  /** 'panel' drops the centred max-width, which is sized for the full page and leaves a 400px
   * column with almost no usable width. */
  layout?: 'page' | 'panel';
  /** Replaces the built-in header. Receives the conversation's resolved title, which arrives late
   * for a new chat because the backend generates it after the first turn, and its visibility. */
  renderHeader?: (
    title: string,
    access?: CommendaAiConversationAccessView
  ) => React.ReactNode;
  /** Rendered directly above the composer. The panel puts its page-context pill here. */
  contextSlot?: React.ReactNode;
  /** Called at the moment a message is sent; the result rides the request as `pageContext`. The
   * panel passes its harvester here; the full page passes nothing and sends no context. */
  getPageContext?: () => CommendaAiPageContext | null;
  /** Where deleting this conversation lands the user. Defaults to the client app's new-chat
   * screen; the affiliate surface points it back at its own chat route. */
  afterDeleteHref?: string;
  /** Fields every send carries, such as the builder's target version. */
  buildSendBody?: () => Partial<SendAgentConversationMessageBody>;
  /** Lifts "a turn is in flight" to the host. The builder gates version polling, canvas sync and
   * its readiness checklist on it, none of which this component knows about. */
  onGeneratingChange?: (isGenerating: boolean) => void;
  /** The transcript as it updates, for a host that shows something computed from it (research's
   * Sources). The host reads what this screen already polls instead of polling the same read again. */
  onMessagesChange?: (messages: readonly UIMessage[]) => void;
  /** Awaited before the turn is posted; false abandons the send. The builder saves the canvas and
   * may fork a new draft version here, both of which have to settle before the turn names one. */
  beforeSend?: () => Promise<boolean>;
}) {
  // Read (and consume) any draft stashed by a prior 401 before it was interrupted.
  // Files dropped anywhere over the chat surface. Handed to the composer, which owns attachment
  // state and the upload, so a drop and the paperclip land in the same place.
  const [droppedFiles, setDroppedFiles] = useState<
    CommendaAiUploadFile[] | null
  >(null);

  const [restoredDraft] = useState(() => {
    if (typeof window === 'undefined') return '';
    const stored =
      window.sessionStorage.getItem(draftKey(conversationId)) ??
      window.sessionStorage.getItem(legacyDraftKey(conversationId));
    window.sessionStorage.removeItem(draftKey(conversationId));
    window.sessionStorage.removeItem(legacyDraftKey(conversationId));
    return stored ?? '';
  });

  // onUnauthorized is passed into the hook that produces `messages`, so read the latest via a ref.
  const messagesRef = useRef<UIMessage[]>(initialMessages);

  const onUnauthorized = useCallback(() => {
    const pending = lastUserText(messagesRef.current);
    if (pending)
      window.sessionStorage.setItem(draftKey(conversationId), pending);
    signOut(undefined, true);
  }, [conversationId]);

  const { user, organizations, navigate, renderConnectorPrompt } =
    useCommendaAiHost();
  const {
    companyId,
    scope,
    isLoading: isConfigLoading,
    workspaceName: workspaceNameOverride,
  } = useCommendaAiWorkspace();
  // A firm-scope conversation has no company to share with, so it is always private.
  const isFirmScope = scope === 'firm';
  const workspaceName =
    workspaceNameOverride ??
    organizations.find((organization) => organization.id === String(companyId))
      ?.name;

  const boundProfileKey = getBoundProfileKey(loadedConversation);
  // Read on every send rather than once, so an open tab follows the user into a new zone.
  const buildTurnSendBody = useCallback(() => {
    const timeZone = getBrowserTimeZone();
    return { ...(timeZone ? { timeZone } : {}), ...buildSendBody?.() };
  }, [buildSendBody]);

  const {
    messages,
    setMessages,
    sendMessage,
    sendTurn,
    regenerate,
    resumeStream,
    status,
    stop,
    error,
    clearError,
  } = useCommendaAiChat(conversationId, initialMessages, {
    onUnauthorized,
    buildSendBody: buildTurnSendBody,
  });

  // A queued run keeps working while nobody is watching, so a reload lands on a transcript that
  // would otherwise sit frozen until the next send. The transport answers null when the run has
  // already settled, which makes this a single cheap read on every other mount.
  useEffect(() => {
    // A first message stashed by the new-chat screen is flushed below and polls its own reply.
    if (window.sessionStorage.getItem(commendaAiPendingKey(conversationId)))
      return;
    resumeStream().catch(() => undefined);
    // Mount only: re-running this on a later render would re-attach to a poll already in flight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // The attachment open in the right-side preview panel (null = closed), and whether the panel is
  // expanded to fill the whole surface (chat hidden) vs docked beside the chat.
  const [previewFile, setPreviewFile] = useState<PreviewFile | null>(null);
  const [isPanelFullscreen, setIsPanelFullscreen] = useState(false);
  const {
    width: panelWidth,
    isResizing: isPanelResizing,
    handleMouseDown: handlePanelResizeMouseDown,
  } = useResizablePane({
    edge: 'left',
    min: PREVIEW_PANEL_MIN_WIDTH,
    max: PREVIEW_PANEL_MAX_WIDTH,
    defaultWidth: PREVIEW_PANEL_DEFAULT_WIDTH,
    storageKey: 'enterprise.commendaAi.previewPanelWidth',
  });
  // True when the feed is scrolled down (content hidden above) → show the top fade gradient.
  const [showTopFade, setShowTopFade] = useState(false);
  const reduceMotion = useReducedMotion() ?? false;

  // Lifted from the Conversation's stick-to-bottom context so posting can force-scroll to the bottom.
  const scrollToBottomRef = useRef<(() => void) | null>(null);
  const registerScrollToBottom = useCallback((fn: () => void) => {
    scrollToBottomRef.current = fn;
  }, []);

  // Title comes from the shared conversation list (same SWR key as the navbar Recents) so the
  // async-generated title lands here too once it's revalidated.
  const {
    conversations,
    isLoading: isConversationsLoading,
    error: conversationsError,
    mutate: mutateConversations,
  } = useCommendaAiConversations(companyId, !isConfigLoading, scope);
  const conversation =
    conversations?.find((candidate) => candidate.id === conversationId) ??
    loadedConversation;
  // "New chat" only once the list has loaded without this conversation in it.
  const title =
    conversation?.title ??
    (isConfigLoading || isConversationsLoading
      ? 'Loading…'
      : conversationsError
        ? 'Chat'
        : 'New chat');
  // Rename and delete are the owner's alone.
  const isOwner = conversation?.userId === user?.id;
  // Firm-scope and company-less conversations cannot be shared, so neither shows the control.
  const isShareable =
    !isFirmScope &&
    !!conversation &&
    ConversationAccessHelper.isShareable(conversation);
  const canChangeVisibility =
    isShareable &&
    ConversationAccessHelper.canChangeVisibility(
      conversation,
      user?.id,
      layout
    );
  const access = isShareable
    ? ConversationAccessHelper.getAccessView(conversation, user?.id, layout)
    : undefined;
  // Only the default chat profile can rerun a turn; the backend refuses regenerate for every other.
  const canRegenerate = boundProfileKey === null;
  // Only the default chat and the builder can be stopped; the backend refuses stop for every other.
  const canStop =
    boundProfileKey === null ||
    boundProfileKey === WORKFLOW_BUILDER_PROFILE_KEY;

  // Verdicts already saved, so thumbs stay filled and the modal prefilled across a reload; this
  // session's ratings win. While this loads or after it fails the thumbs show unrated and still
  // work, so it is not gated.
  const { feedbackByMessageId, revalidateFeedback } =
    useCommendaAiConversationFeedback(
      conversationId,
      companyId,
      !isConfigLoading
    );

  // Delete → optimistically drop from the Recents list, then return to the new-chat screen.
  const handleDelete = useCallback(() => {
    mutateConversations(
      async (current) => {
        const deleted = await deleteCommendaAiConversation(
          conversationId,
          companyId,
          scope
        );
        if (!deleted.success) throw new Error(deleted.error);
        return (current ?? []).filter((c) => c.id !== conversationId);
      },
      {
        optimisticData: (current) =>
          (current ?? []).filter((c) => c.id !== conversationId),
        rollbackOnError: true,
        revalidate: false,
      }
    ).catch(() => toast.error('Could not delete the chat.'));
    navigate(afterDeleteHref);
  }, [
    conversationId,
    companyId,
    scope,
    mutateConversations,
    navigate,
    afterDeleteHref,
  ]);

  const handleRename = useCallback(
    (nextTitle: string) => {
      const next = nextTitle.trim();
      if (!next) return;
      mutateConversations(
        async (current) => {
          const renamed = await renameCommendaAiConversation(
            conversationId,
            next,
            companyId,
            scope
          );
          if (!renamed.success) throw new Error(renamed.error);
          return (current ?? []).map((c) =>
            c.id === conversationId ? { ...c, title: next } : c
          );
        },
        {
          optimisticData: (current) =>
            (current ?? []).map((c) =>
              c.id === conversationId ? { ...c, title: next } : c
            ),
          rollbackOnError: true,
          revalidate: false,
        }
      ).catch(() => toast.error('Could not rename the chat.'));
    },
    [conversationId, companyId, scope, mutateConversations]
  );

  const handleVisibilityChange = useCallback(
    (visibility: CommendaAiConversationVisibility) => {
      const apply = (current: CommendaAiConversationRecord[] | undefined) =>
        (current ?? []).map((c) =>
          c.id === conversationId ? { ...c, visibility } : c
        );
      mutateConversations(
        async (current) => {
          const updated = await updateCommendaAiConversationVisibility(
            conversationId,
            visibility
          );
          if (!updated.success) throw new Error(updated.error);
          return apply(current);
        },
        { optimisticData: apply, rollbackOnError: true, revalidate: false }
      ).catch(() => toast.error('Could not change who can see this chat.'));
    },
    [conversationId, mutateConversations]
  );

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Flush the first message stashed by the new-chat screen (create-on-send). Removing the key up
  // front + the ref make this fire exactly once for the freshly-created conversation.
  const pendingFlushedRef = useRef(false);
  useEffect(() => {
    if (pendingFlushedRef.current) return;
    const raw = window.sessionStorage.getItem(
      commendaAiPendingKey(conversationId)
    );
    if (!raw) return;
    window.sessionStorage.removeItem(commendaAiPendingKey(conversationId));
    pendingFlushedRef.current = true;

    let text = raw;
    let attachments: AgentAttachment[] = [];
    try {
      const parsed = JSON.parse(raw) as {
        text?: string;
        attachments?: AgentAttachment[];
      };
      text = parsed.text ?? '';
      attachments = parsed.attachments ?? [];
    } catch {
      // Legacy plain-text pending — treat the raw value as the message text.
    }

    // Harvested at flush time, not at stash time: the flush runs on the same page a beat later,
    // and this is the moment the request actually leaves.
    const pageContext = getPageContext?.() ?? null;

    if (text.trim())
      sendMessage(
        { text, files: toFileParts(attachments) },
        pageContext ? { body: { pageContext } } : undefined
      ).catch(() => undefined);
  }, [conversationId, sendMessage, getPageContext]);

  /** The client abort alone leaves the worker running: it finishes the turn and persists it, so a
   * reload shows content that arrived after the user pressed stop. Tell the server too. */
  const handleStop = useCallback(() => {
    stop();
    stopAgentConversation(conversationId)
      .then((stopped) => {
        if (!stopped.success) toast.error('Could not stop the reply.');
      })
      .catch(() => undefined);
  }, [stop, conversationId]);

  const handleSubmit = useCallback(
    async (
      text: string,
      attachments: AgentAttachment[],
      dataRoom?: IDocumentReaderUploadReference
    ) => {
      // Awaited, not fired alongside: the builder forks a draft version here, and a turn posted
      // before the fork settles would name a version the server refuses.
      // A rejection keeps the composer's text and files.
      if (beforeSend && !(await beforeSend()))
        throw new Error('The send was held back.');

      const pageContext = getPageContext?.() ?? null;

      // A data room rides as its id in the body (the transport merges it) and as the same data part the
      // backend stores, so the pill shows before the reload too. Without one the send is today's.
      const sent = dataRoom
        ? sendTurn(
            {
              parts: [
                { type: 'text', text },
                ...toFileParts(attachments),
                { type: 'data-document-upload', data: dataRoom },
              ],
            },
            {
              body: {
                ...(pageContext ? { pageContext } : {}),
                documentUploadId: dataRoom.uploadId,
              },
            }
          )
        : sendTurn(
            { text, files: toFileParts(attachments) },
            pageContext ? { body: { pageContext } } : undefined
          );
      // Jump to the newest content on post, even if the user had scrolled up — the resize observer
      // then keeps them pinned as the reply streams in.
      scrollToBottomRef.current?.();

      try {
        await sent;
      } catch (sendError) {
        // The server never stored the turn, so its bubble and error go and the composer keeps the
        // text: Retry is not offered for every agent, and a resend would show the turn twice.
        setMessages((current) =>
          current.at(-1)?.role === 'user' ? current.slice(0, -1) : current
        );
        clearError();
        toast.error("Couldn't send your message. Try again.");
        throw sendError;
      }
    },
    [sendTurn, getPageContext, beforeSend, setMessages, clearError]
  );

  // A turn is "in flight" from submit until the stream ends.
  const isGeneratingTurn = status === 'submitted' || status === 'streaming';

  useDocumentProcessingNotice({
    conversationId,
    isIdle: status === 'ready' || status === 'error',
    setMessages,
  });

  useEffect(() => {
    onGeneratingChange?.(isGeneratingTurn);
  }, [isGeneratingTurn, onGeneratingChange]);

  useEffect(() => {
    onMessagesChange?.(messages);
  }, [messages, onMessagesChange]);

  // Questions awaiting an answer, in the order they claimed the slot. Only the first is docked; a
  // turn that asks twice shows the second once the first is resolved, rather than losing the first.
  const [liveCards, setLiveCards] = useState<CommendaAiLiveCard[]>([]);
  const liveCard = liveCards[0] ?? null;
  const setLiveCard = useCallback((card: CommendaAiLiveCard) => {
    setLiveCards((current) =>
      current.some((queued) => queued.toolCallId === card.toolCallId)
        ? current.map((queued) =>
            queued.toolCallId === card.toolCallId ? card : queued
          )
        : [...current, card]
    );
  }, []);
  const clearLiveCard = useCallback((toolCallId: string) => {
    setLiveCards((current) =>
      current.filter((queued) => queued.toolCallId !== toolCallId)
    );
  }, []);

  // Answers given in this session. The server stores the answer on the asking message, but history
  // loads once at mount and never refetches, so this is what collapses the card immediately.
  const [answeredInSession, setAnsweredInSession] = useState<
    Record<string, CommendaAiQuestionAnswer[]>
  >({});

  // The card answer whose send is in flight. useChat's sendMessage never rejects: a failed request
  // (network, or the backend's 409 for an already-answered question) lands in `status === 'error'`
  // instead. When that happens the record does not carry this answer, so the optimistic entry and
  // the optimistic user message are both dropped: the asking message has no reply after it again,
  // so the card re-docks and answering it is the retry. The error surfaces like any failed send.
  const inFlightAnswerRef = useRef<string | null>(null);
  const dropAnsweredInSession = useCallback((toolCallId: string) => {
    setAnsweredInSession((current) => {
      if (!(toolCallId in current)) return current;
      return Object.fromEntries(
        Object.entries(current).filter(([key]) => key !== toolCallId)
      );
    });
  }, []);
  useEffect(() => {
    if (status === 'submitted' || status === 'streaming') return;

    const toolCallId = inFlightAnswerRef.current;
    inFlightAnswerRef.current = null;
    if (toolCallId === null || status !== 'error') return;

    dropAnsweredInSession(toolCallId);
    setMessages((current) =>
      current.filter(
        (message) =>
          !(
            message.role === 'user' &&
            QuestionBlockHelper.answeredToolCallId(message.parts) === toolCallId
          )
      )
    );
  }, [status, setMessages, dropAnsweredInSession]);

  const submitQuestionAnswer = useCallback(
    async (payload: {
      toolCallId: string;
      answers: CommendaAiQuestionAnswer[];
      text: string;
      attachments?: AgentAttachment[];
    }) => {
      // Awaited first, exactly as a typed send is: an answer starts a turn too, and the builder saves
      // or forks its canvas here. Refused, the card stays open for another try.
      if (beforeSend && !(await beforeSend())) return;

      setAnsweredInSession((current) => ({
        ...current,
        [payload.toolCallId]: payload.answers,
      }));
      inFlightAnswerRef.current = payload.toolCallId;

      const pageContext = getPageContext?.() ?? null;

      // Typed here rather than inline, so the field this rides on stays the same one both sends
      // declare: the streaming route's own body and the unified send's `answerTo`.
      const answerTo: AgentConversationAnswerTo = {
        toolCallId: payload.toolCallId,
        answers: payload.answers,
      };

      // The marker part is client-only: the transport sends `text`, `attachments` and `body`, and
      // the backend stores its own marker on the user message from `body.answerTo`. Putting the
      // same marker on the optimistic message useChat keeps locally is what hides the bubble now,
      // the way the stored one hides it on reload.
      const sendAsNextTurn = () =>
        sendMessage(
          {
            parts: [
              { type: 'text', text: payload.text },
              ...toFileParts(payload.attachments ?? []),
              {
                type: QuestionBlockHelper.QUESTION_ANSWER_PART_TYPE,
                data: { toolCallId: payload.toolCallId },
              },
            ],
          },
          { body: { ...(pageContext ? { pageContext } : {}), answerTo } }
        );

      // A failed POST re-docks the card with an error. Once the POST lands the server holds the
      // answer, so a failed reconnect after it must not roll the answer back.
      const answerPausedRun = async () => {
        const sent = await sendAgentConversationMessage(conversationId, {
          message: payload.text,
          answerTo,
        });
        inFlightAnswerRef.current = null;
        if (!sent.success) {
          dropAnsweredInSession(payload.toolCallId);
          toast.error("Couldn't send your answer. Try again.");
          return;
        }
        await resumeStream().catch(() => undefined);
      };

      // Every agent's answer goes through the same send with `answerTo`. What differs is what comes
      // back: a run paused at the question carries on in the reply that asked it, so that reply is
      // resumed; any other answer is the next turn. The read says which, so no agent is named here.
      readAgentConversation(conversationId)
        .then(
          (envelope) =>
            envelope.pendingGate ? answerPausedRun() : sendAsNextTurn(),
          () => sendAsNextTurn()
        )
        .catch(() => undefined);
      scrollToBottomRef.current?.();
    },
    [
      sendMessage,
      getPageContext,
      resumeStream,
      conversationId,
      beforeSend,
      dropAnsweredInSession,
    ]
  );

  // Question cards send through these, so a card answer takes the exact composer path
  // (page context, file parts, force-scroll).
  const chatActions = useMemo<CommendaAiActions>(
    () => ({
      submit: handleSubmit,
      submitQuestionAnswer,
      answeredInSession,
      isGenerating: isGeneratingTurn,
      setLiveCard,
      clearLiveCard,
    }),
    [
      handleSubmit,
      submitQuestionAnswer,
      answeredInSession,
      isGeneratingTurn,
      setLiveCard,
      clearLiveCard,
    ]
  );

  const followingUserTexts = useMemo(
    () => followingUserTextByIndex(messages),
    [messages]
  );

  // Keyed by content so a streamed token doesn't hand every message a new plugin list.
  const documentMentionsKey = useMemo(
    () => JSON.stringify(ChatDocumentMentionsHelper.collect(messages)),
    [messages]
  );
  const documentMentions = useMemo(
    () => JSON.parse(documentMentionsKey) as IChatDocumentMention[],
    [documentMentionsKey]
  );

  // The client app's OAuth connector card, when the last turn asked the reader to authorize an
  // app. A surface whose host supplies none renders nothing here.
  const connectorPrompt = renderConnectorPrompt?.(
    isGeneratingTurn ? null : messages
  );

  useEffect(() => {
    if (isGeneratingTurn) return undefined;

    const timers = TITLE_REVALIDATE_DELAYS_MS.map((delay) =>
      setTimeout(() => {
        mutateConversations().catch(() => undefined);
      }, delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [isGeneratingTurn, mutateConversations]);

  const lastMessage = messages[messages.length - 1];
  const liveParts =
    isGeneratingTurn && lastMessage?.role === 'assistant'
      ? lastMessage.parts.filter((part) => part.type !== 'file')
      : [];
  const showThinkingIndicator = isGeneratingTurn;
  const isTurnStart = isGeneratingTurn && liveParts.length === 0;

  return (
    <CommendaAiActionsContext.Provider value={chatActions}>
      <ChatDropZone
        className="flex h-full min-h-0"
        onFilesDropped={setDroppedFiles}
      >
        {/* Chat column stays mounted, in-flow and never resized to zero — when the panel goes
          full screen it is covered by an absolute overlay (below) rather than hidden — so the
          conversation scroll container always keeps its scroll position. The data attribute is the
          keyboard scope for question-card number shortcuts (see useQuestionCardHotkeys). */}
        <div className="flex min-w-0 flex-1 flex-col" data-commenda-ai-surface>
          {/* Header sits above the chat column only; the panel supplies its own. */}
          {renderHeader ? (
            renderHeader(title, access)
          ) : (
            <ConversationHeader
              title={title}
              access={access}
              workspaceName={workspaceName}
              onDelete={isOwner ? handleDelete : undefined}
              onRename={isOwner ? handleRename : undefined}
              onVisibilityChange={
                canChangeVisibility ? handleVisibilityChange : undefined
              }
              trailing={headerTrailing}
            />
          )}
          {/* Full width so the scroll container's scrollbar sits at the container's right edge; the
            max-width lives on the scrolled content and the composer instead. */}
          <div className="flex min-h-0 w-full flex-1 flex-col">
            <div className="relative flex min-h-0 flex-1 flex-col">
              {/* Fades content scrolled above the viewport; only visible once scrolled from the top. */}
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-x-0 top-0 z-10 h-12 bg-gradient-to-b from-bg-light to-transparent transition-opacity duration-200',
                  showTopFade ? 'opacity-100' : 'opacity-0'
                )}
              />
              {/* Mirrors the top fade at the bottom edge so content fades into the chat input. */}
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-12 bg-gradient-to-t from-bg-light to-transparent"
              />
              <Conversation className="flex-1" initial="instant">
                <ScrollToBottomBridge register={registerScrollToBottom} />
                <ConversationTopFadeTracker onChange={setShowTopFade} />
                <ConversationContent
                  className={cn(
                    'mx-auto w-full pb-10',
                    layout === 'page' ? 'max-w-3xl' : 'px-lg'
                  )}
                >
                  {messages.map((message, messageIndex) => {
                    const isLastMessage = messageIndex === messages.length - 1;
                    return (
                      <CommendaAiMessageItem
                        key={message.id}
                        conversationId={conversationId}
                        message={message}
                        isLastMessage={isLastMessage}
                        isGenerating={isGeneratingTurn}
                        followingUserMessageText={
                          followingUserTexts[messageIndex] ?? null
                        }
                        signedInUserName={user?.name ?? null}
                        initialFeedback={
                          feedbackByMessageId.get(message.id) ?? null
                        }
                        onFeedbackSaved={revalidateFeedback}
                        onRegenerate={
                          isLastMessage && canRegenerate
                            ? () => regenerate().catch(() => undefined)
                            : undefined
                        }
                        onPreviewFile={setPreviewFile}
                        documentMentions={documentMentions}
                      />
                    );
                  })}
                  {showThinkingIndicator && (
                    <Message from="assistant">
                      <MessageContent>
                        <div className="flex items-center gap-2">
                          {/* Previous thinking indicator, kept for reference:
                          <span
                            className="flex size-14 shrink-0 items-center justify-center overflow-hidden"
                            role="status"
                            aria-label="Assistant is thinking"
                          >
                            <Loading size={140} fit="cover" />
                          </span>
                          */}
                          <span
                            role="status"
                            aria-label="Assistant is thinking"
                          >
                            <ThinkingOrb size={40} />
                          </span>
                          {isTurnStart && (
                            <Shimmer className="text-sm">Thinking…</Shimmer>
                          )}
                        </div>
                      </MessageContent>
                    </Message>
                  )}
                  {error ? (
                    <div className="flex items-center gap-3 px-4 py-2">
                      <span className="text-chat-destructive text-sm">
                        Something went wrong.
                      </span>
                      {/* A failed card answer retries through the re-docked card; regenerating
                          would resend without `answerTo`. */}
                      {liveCard === null && canRegenerate && (
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            regenerate().catch(() => undefined);
                          }}
                          className="border-chat-border rounded-md px-2 py-1 text-xs font-medium"
                        >
                          Retry
                        </Button>
                      )}
                    </div>
                  ) : null}
                </ConversationContent>
                <ConversationScrollButton className="bottom-10" />
              </Conversation>
            </div>

            <div
              className={cn(
                'mx-auto flex w-full flex-col gap-lg pb-xl',
                layout === 'page' ? 'max-w-3xl px-xl' : 'px-lg'
              )}
            >
              {liveCard ? (
                <QuestionDockedCard
                  key={liveCard.toolCallId}
                  card={liveCard}
                  isGenerating={isGeneratingTurn}
                  onAnswer={submitQuestionAnswer}
                />
              ) : null}
              {connectorPrompt}
              <ComposerClientStrip composerControls={composerControls} />
              <ChatComposer
                placeholder="Describe your task or ask a question"
                contextSlot={contextSlot}
                showDisclaimer={layout === 'page'}
                filesToIngest={droppedFiles}
                onFilesIngested={() => setDroppedFiles(null)}
                onSubmit={handleSubmit}
                allowAttachments={profileTakesAttachments(boundProfileKey)}
                dataRoomTarget={
                  boundProfileKey === null ||
                  boundProfileKey === COMMENDA_CHAT_PROFILE_KEY
                    ? { conversationId, companyId }
                    : undefined
                }
                status={status}
                onStop={canStop ? handleStop : undefined}
                initialInput={restoredDraft}
                toolsSlot={
                  composerControls &&
                  ComposerControlsHelper.hasFooterControls(composerControls) ? (
                    <ComposerFooterControls
                      composerControls={composerControls}
                      boundProfile={{
                        profileKey: boundProfileKey,
                      }}
                    />
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>
        {/* Enter/exit: slide in from the right (where it lives) + fade, iOS-drawer ease-out, a touch
          under 300ms with a snappier exit. Only transform+opacity animate — the chat's flex reflow
          is instant (animating width would jank). Reduced motion → fade only. */}
        <AnimatePresence>
          {previewFile && (
            <motion.div
              key="attachment-preview-panel"
              initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={
                reduceMotion
                  ? { opacity: 0, transition: { duration: 0.15 } }
                  : {
                      opacity: 0,
                      x: 24,
                      transition: { duration: 0.2, ease: PANEL_EASE },
                    }
              }
              transition={{ duration: 0.28, ease: PANEL_EASE }}
              style={isPanelFullscreen ? undefined : { width: panelWidth }}
              className={cn(
                'relative flex min-h-0 min-w-0 p-md',
                // Docked: an in-flow sibling the reader can widen by dragging its left edge. Full
                // screen: an absolute overlay on top of the chat (bg fills the p-md gap) so the chat
                // column underneath is never resized or hidden.
                isPanelFullscreen
                  ? 'absolute inset-0 z-10 bg-bg-light'
                  : 'shrink-0'
              )}
            >
              {!isPanelFullscreen && (
                <GripResizeHandle
                  ariaLabel="Resize preview panel"
                  isActive={isPanelResizing}
                  onMouseDown={handlePanelResizeMouseDown}
                  width={panelWidth}
                  min={PREVIEW_PANEL_MIN_WIDTH}
                  max={PREVIEW_PANEL_MAX_WIDTH}
                />
              )}
              <AttachmentPreviewPanel
                file={previewFile}
                isFullscreen={isPanelFullscreen}
                onToggleFullscreen={() => setIsPanelFullscreen((v) => !v)}
                onClose={() => {
                  setPreviewFile(null);
                  setIsPanelFullscreen(false);
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </ChatDropZone>
    </CommendaAiActionsContext.Provider>
  );
}
